<template>
  <base-dialog
    :title="'Business verification details'"
    :action="onAction"
    :action-name="'Update'"
    :close="onClose"
  >
    <template #activator="{ on }">
      <slot
        name="activator"
        v-bind="{ on }"
      />
    </template>

    <template #content>
      <v-row
        justify="center"
        align="center"
      >
        <v-col cols="10">
          <div class="title mb-3">Legal Business Details</div>
          <v-text-field
            outlined
            dense
            v-model="model.company.name"
            label="Legal Business Name"
            type="text"
          />
          <international-phone-number
            :init="model.company.phone || ''"
            @change="onPhoneNumberChange"
          />
          <v-text-field
            outlined
            dense
            v-if="!model.company.tax_id_provided"
            v-model="model.company.tax_id"
            label="Tax ID"
            type="text"
            persistent-hint
            hint="This value will never be displayed in the application"
          />
          <div
            v-if="model.company.tax_id_provided"
            class="grey--text"
          >
            Tax ID Provided
            <br />
          </div>

          <span
            v-if="model.tos_shown_and_accepted !== false && model.tos_shown_and_accepted > 1"
            class="grey--text"
          >
            {{ `Agreed on ${_formatDate(model.tos_shown_and_accepted)}` }}
          </span>
          <v-checkbox
            v-if="model.tos_shown_and_accepted === false || model.tos_shown_and_accepted === true"
            v-model="model.tos_shown_and_accepted"
            color="primary"
          >
            <template v-slot:label>
              <div>
                By checking this box, you agree to our
                Services Agreement and the

                <a
                  target="_blank"
                  href="https://stripe.com/connect-account/legal"
                  @click.stop
                >
                  Stripe Connected Account Agreement
                </a>
                .
              </div>
            </template>
          </v-checkbox>
          <div v-else>
            <a
              target="_blank"
              href="https://stripe.com/connect-account/legal"
              @click.stop
            >
              Stripe Connected Account Agreement
            </a>
          </div>

          <v-divider class="my-5"/>

          <div class="title mb-3">Address</div>
          <v-text-field
            outlined
            dense
            v-model="model.company.address.line1"
            label="Line 1"
            type="text"
          />
          <v-text-field
            outlined
            dense
            v-model="model.company.address.line2"
            label="Line 2"
            type="text"
          />
          <v-text-field
            outlined
            dense
            v-model="model.company.address.city"
            label="City"
            type="text"
          />
          <v-text-field
            outlined
            dense
            v-model="model.company.address.state"
            label="Province / State"
            type="text"
          />
          <v-text-field
            outlined
            dense
            v-model="model.company.address.postal_code"
            label="Postal Code"
            type="text"
          />

          <v-divider class="my-5"/>

          <div class="title mb-3">Public Profile </div>

          <v-text-field
            outlined
            dense
            v-model="model.business_profile.name"
            label="Public Name"
            type="text"
          />
          <v-textarea
            outlined
            dense
            v-model="model.business_profile.product_description"
            label="Public Description"
          />

          <v-text-field
            outlined
            dense
            v-model="model.business_profile.url"
            label="Public Website"
            type="text"
          />
          <h3 class="mb-3 font-weight-thin">Social Media</h3>
          <v-text-field
            outlined
            dense
            v-model="model.metadata.social_facebook"
            label="Facebook"
            type="text"
          />
          <v-text-field
            outlined
            dense
            v-model="model.metadata.social_twitter"
            label="Twitter"
            type="text"
          />
          <v-text-field
            outlined
            dense
            v-model="model.metadata.social_instagram"
            label="Instagram"
            type="text"
          />
          <v-text-field
            outlined
            dense
            v-model="model.metadata.social_youtube"
            label="Youtube"
            type="text"
          />

          <h3 class="mb-3 font-weight-thin">Support Details</h3>
          <v-text-field
            outlined
            dense
            v-model="model.business_profile.support_email"
            label="Support Email"
            type="email"
          />

          <international-phone-number
            :init="model.business_profile.support_phone || ''"
            @change="val => { model.business_profile.support_phone = val.number }"
          />

          <v-text-field
            outlined
            dense
            v-model="model.business_profile.support_url"
            label="Support Website"
            type="text"
          />



        </v-col>
      </v-row>
    </template>
  </base-dialog>
</template>
<script>
import { mapActions } from 'vuex'
import { get, isEmpty, isNumber } from 'lodash'
import BaseDialog from "../Common/BaseDialog"
import { getAccountToken } from "../../services/stripe.service"
import InternationalPhoneNumber from "../Common/InternationalPhoneNumber.vue"
import { formatDate, isErrorResponse } from "../../services/util.service"

export default {
  components: {
    InternationalPhoneNumber,
    BaseDialog,
  },
  props: {
    account: {
      type: Object,
      default: () => ({})
    },
  },
  created() {
    this.initAccount = { ...this.account }
    this.resetModel()
  },
  data() {
    return {
      initAccount: {},
      model: {
        metadata: {
          social_facebook: '',
          social_twitter: '',
          social_instagram: '',
          social_youtube: '',
        },
        business_profile: {
          mcc: '', // Merchant Category Code
          name: '',
          product_description: '',
          support_email: '',
          support_phone: '',
          support_url: '',
          url: ''
        },
        tos_shown_and_accepted: '',
        email: '',
        company: {
          tax_id_provided: false,
          name: '',
          tax_id: undefined, // never returned, see tax_id_provided.
          phone: '',
          address: {
            line1: '',
            line2: '',
            city: '',
            state: '',
            postal_code: '',
            country: '',
          },
        }
      }
    }
  },
  methods: {
    ...mapActions([ 'updateAccountRequirements', 'afterLogin' ]),
    _formatDate: val => formatDate(val),
    onPhoneNumberChange(phoneObject) {
      const { number } = phoneObject || {}

      if (number) {
        this.model.company.phone = number
      }
    },
    resetModel() {
      this.model = { ...this.initAccount }
      this.model.tos_shown_and_accepted = get(this.initAccount, 'tos_acceptance.date', false)
      this.model.company = { ...this.initAccount.company }
      this.model.company.address = { ...get(this.initAccount, 'company.address', {}) }
      this.model.business_profile = { ...get(this.initAccount, 'business_profile', {}) }
      this.model.metadata = { ...get(this.initAccount, 'metadata', {}) }
    },
    onClose() {
      this.resetModel()
      this.$emit('close')
    },
    onAction() {
      let tos_shown_and_accepted
      if (this.model.tos_shown_and_accepted > 0 || this.model.tos_shown_and_accepted === true) {
        tos_shown_and_accepted = true
      }

      // hard coded as company for now
      const accountPayload = {
        business_type: 'company',
        company: {
          address: {
            line1: get(this.model, 'company.address.line1') || undefined,
            line2: get(this.model, 'company.address.line2') || undefined,
            city: get(this.model, 'company.address.city') || undefined,
            state: get(this.model, 'company.address.state') || undefined,
            postal_code: get(this.model, 'company.address.postal_code') || undefined,
          },
          name: this.model.company.name || undefined,
          phone: this.model.company.phone || undefined,
          tax_id: this.model.company.tax_id_provided ? undefined : this.model.company.tax_id,
        },
        tos_shown_and_accepted,
        requested_capabilities: [
          'card_payments',
          'transfers',
        ],
      }

      const doUpdateAccount = (result) => {
        const { token, error } = result

        if (error) {
          this.$store.dispatch('snackError', error)
          return { status: 400 }
        } else {

        }

        const accountTokenId = token.id
        const requirementsPayload = {
          account_token: accountTokenId,
          metadata:{
            social_facebook: get(this.model, 'metadata.social_facebook', ''),
            social_twitter: get(this.model, 'metadata.social_twitter', ''),
            social_instagram: get(this.model, 'metadata.social_instagram', ''),
            social_youtube: get(this.model, 'metadata.social_youtube', ''),
          },
          business_profile: {
            mcc: get(this.model, 'business_profile.mcc') || undefined,
            name: get(this.model, 'business_profile.name') || undefined,
            product_description: get(this.model, 'business_profile.product_description') || undefined,
            support_email: get(this.model, 'business_profile.support_email') || undefined,
            support_phone: get(this.model, 'business_profile.support_phone') || undefined,
            support_url: get(this.model, 'business_profile.support_url') || undefined,
            url: get(this.model, 'business_profile.url') || undefined
          }
        }


        return this.updateAccountRequirements(requirementsPayload)
          .then(account => {
            if (!isErrorResponse(account)) {
              this.$store.dispatch('snackBar', { color: 'success', text: 'Account updated' })
              // Since the account was updated and its attached to our token for api calls
              // we need to refresh our token.
              const afterLoginPayload = {
                silentLoading: true
              }
              this.afterLogin(afterLoginPayload)

              this.initAccount = { ...account }
              this.resetModel()
              this.$emit('action', account)
            }

            return account
          })

      }

      return getAccountToken(accountPayload)
        .then(doUpdateAccount)
        .catch(e => {
          this.$store.dispatch('snackError', e)
          return e
        })
    }
  }
}
</script>

